import TableWithTitle from '@/components/TableWithTitle.vue';
import WrapWithPopover from "@/components/shared/WrapWithPopover.vue";
import { getDescriptionBasedOnKpiKey, getColorsBasedOnRange, transformGoogleSheetFromCSV } from "@/utils/transformers";
import { SELECTOR, PERIOD_FIELD, ORDERED_REVENUE_FIELD, SHIPPED_REVENUE_FIELD, SHIPPED_COGS_FIELD, CUSTOMER_RETURNS_FIELD, NET_PPM_FIELD, NET_PPM_PERCENTAGE_FIELD, NET_RECEIVED_UNITS_FIELD } from "@/utils/table_fields";
import AppIcon from '@/components/AppIcon.vue';
import { mapState } from "vuex";
import { HugValue, DateValue, CurrencyValue, PercentageValue, BarWithValue, OverlapBarValue } from "@/components/tableElements";
import LiteTableMixin from '@/mixins/LiteTableMixin.vue';
import { DEFAULT_DATE_FORMATS, PERIOD_COLUMN_TITLES, COLORS } from '@/utils/constants';
import FlexRow from '@/components/FlexRow.vue';
import moment from 'moment';
import { capitalize } from 'vue';
import SlotWithEvolutionValue from "@/components/tableElements/SlotWithEvolutionValue.vue";
import EvolutionValue from "@/components/tableElements/EvolutionValue.vue";
import WrapTableWithShowMore from '@/components/WrapTableWithShowMore.vue';
import TextValue from '@/components/tableElements/TextValue.vue';
import VariableStyledBar from "@/components/tableElements/VariableStyledBar.vue";
import LayerBars from "@/components/tableElements/LayerBars.vue";
import BarSlotWithValue from "@/components/tableElements/BarSlotWithValue.vue";
import TasksMixin from "@/mixins/TasksMixin.vue";
import DownloadMixin from "@/mixins/DownloadMixin.vue";
export default {
  name: "VendorPeriodTable",
  components: {
    TableWithTitle,
    WrapWithPopover,
    AppIcon,
    HugValue,
    DateValue,
    FlexRow,
    SlotWithEvolutionValue,
    EvolutionValue,
    WrapTableWithShowMore,
    CurrencyValue,
    PercentageValue,
    BarWithValue,
    OverlapBarValue,
    VariableStyledBar,
    LayerBars,
    BarSlotWithValue
  },
  mixins: [LiteTableMixin, DownloadMixin, TasksMixin],
  props: {
    loading: {
      typ: Boolean,
      required: true
    }
  },
  emits: ["setDateRange", "refresh"],
  data() {
    return {
      sheetGenerationTaskIdentifier: "",
      PERIOD_COLUMN_TITLES,
      COLORS,
      fields: [SELECTOR, PERIOD_FIELD, ORDERED_REVENUE_FIELD, SHIPPED_REVENUE_FIELD, SHIPPED_COGS_FIELD, CUSTOMER_RETURNS_FIELD, NET_PPM_FIELD, NET_PPM_PERCENTAGE_FIELD, NET_RECEIVED_UNITS_FIELD],
      csvSources: period => ({
        [PERIOD_COLUMN_TITLES[period]]: PERIOD_FIELD.key,
        [ORDERED_REVENUE_FIELD.label]: ORDERED_REVENUE_FIELD.key,
        ...this.getEvolutionsForField(ORDERED_REVENUE_FIELD, {
          suffixes: this.evolCategory
        }),
        [SHIPPED_REVENUE_FIELD.label]: SHIPPED_REVENUE_FIELD.key,
        ...this.getEvolutionsForField(SHIPPED_REVENUE_FIELD, {
          suffixes: this.evolCategory
        }),
        [SHIPPED_COGS_FIELD.label]: SHIPPED_COGS_FIELD.key,
        ...this.getEvolutionsForField(SHIPPED_COGS_FIELD, {
          suffixes: this.evolCategory
        }),
        [CUSTOMER_RETURNS_FIELD.label]: CUSTOMER_RETURNS_FIELD.key,
        [NET_PPM_FIELD.label]: NET_PPM_FIELD.key,
        [NET_PPM_PERCENTAGE_FIELD.label]: NET_PPM_PERCENTAGE_FIELD.key,
        ...this.getEvolutionsForField(NET_PPM_PERCENTAGE_FIELD, {
          suffixes: this.evolCategory
        }),
        [NET_RECEIVED_UNITS_FIELD.label]: NET_RECEIVED_UNITS_FIELD.key,
        ...this.getEvolutionsForField(NET_RECEIVED_UNITS_FIELD, {
          suffixes: this.evolCategory
        })
      }),
      evolCategory: {
        prev_period: "_vs_previous_period",
        same_period_ly: "_vs_last_year"
      }
    };
  },
  computed: {
    ...mapState({
      periodTable: state => state.dashboard_v2.periodTable,
      tab: state => state.dashboard_v2.currentTabValue,
      period: state => state.dashboard_v2.period,
      currentPeriod: state => state.dashboard_v2.currentPeriod,
      periodCategory: state => state.dashboard_v2.periodCategory
    }),
    items() {
      return this.periodTable[this.tab].items;
    },
    maxCharCount() {
      return this.periodTable[this.tab].maxCharCount;
    },
    minMax() {
      return this.periodTable[this.tab].minMax;
    },
    getDateValueFormat() {
      // Convert the current period to UPPER CASE
      const period = this.period.toUpperCase();
      return DEFAULT_DATE_FORMATS[period];
    },
    getPeriodColumnTitle() {
      return PERIOD_COLUMN_TITLES[this.period];
    },
    csvFileName() {
      return `Dashboard_Vendor_Evolution_${capitalize(this.period)}.csv`;
    },
    period_pager: {
      get() {
        return this.periodTable.period_page;
      },
      set(_) {
        this.$store.dispatch('dashboardV2IncrementPeriodPage');
        this.$emit('refresh');
      }
    }
  },
  methods: {
    getDescriptionBasedOnKpiKey,
    getColorsBasedOnRange,
    handleDateClick(period) {
      period = moment(period);
      this.$emit("setDateRange", period);
    },
    handleDateReset() {
      this.$emit("setDateRange", null);
    },
    handleRowClick(item) {
      this.handleRowClicked(item, this.handleDateReset, this.handleDateClick, 'period_agg');
    },
    async downloadPerformancePeriodCSV() {
      const csv_headers = this.csvSources(this.period);
      const csvFileName = this.csvFileName;
      const str = await this.generateCsvString(csv_headers, this.prepareCsvRowsForDownload(this.items, csv_headers), 1, "-");
      await this.downloadCsv(str, csvFileName);
      this.trackDashboardCsvExport();
    },
    async copyPerformancePeriodCSV() {
      const csv_headers = this.csvSources(this.period);
      const str = await this.generateCsvString(csv_headers, this.prepareCsvRowsForDownload(this.items, csv_headers), 1, "-");
      await this.copyCSV(str);
    },
    async downloadPerformanceGoogleSheet() {
      const csv_headers = this.csvSources(this.period);
      const rowData = this.prepareCsvRowsForDownload(this.items, csv_headers);
      const data = transformGoogleSheetFromCSV(csv_headers, rowData);
      const task = await this.downloadCSVWithGoogleSheets("/dashboard/v2/generate/googlesheet", {
        sheet_name: this.csvFileName,
        task_type: "google_sheet_generation",
        data
      });

      //console.log('dashboard task', task);

      if (!task) {
        return;
      }
      this.sheetGenerationTaskIdentifier = task.task_id;
      this.executeTaskStatusHandler(task);
      this.checkTaskStatusPeriodically(this.sheetGenerationTaskIdentifier);
    },
    handleRunning() {
      this.checkTaskStatusPeriodically(this.sheetGenerationTaskIdentifier);
    },
    handleFailed(data) {
      this.exportLoading = false;
      if (this.asyncUpdateInterval) {
        clearTimeout(this.asyncUpdateInterval);
        this.asyncUpdateInterval = null;
      }
      window.$bus.trigger("showDanger", {
        visible: true,
        message: "Google sheet generation Task failed \n" + data.task_result,
        delay: 60 // Show alert less than 60 secs
      });
      throw new Error("Google sheet generation Task failed");
    },
    async handleFinished(data) {
      //console.log('dashboard finished', data);
      if (data) {
        const {
          task_result: {
            share_link
          }
        } = data;
        if (this.asyncUpdateInterval) {
          clearTimeout(this.asyncUpdateInterval);
          this.asyncUpdateInterval = null;
        }
        window.$bus.trigger("showSuccess", {
          visible: true,
          message: "The google sheet has been successfully generated. A link has been sent to your email.",
          delay: 60 // Show alert less than 60 secs
        });
        this.exportLoading = false;
        if (share_link) window.open(share_link, "_blank");
      }
    }
  }
};